import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export const GUEST_TYPES = ["Regular", "VIP", "Employee", "Sponsor", "General"];
export const GUEST_TYPE_BADGE = {
  Regular: "bg-teal-500/20 text-teal-300 border-teal-500/40",
  VIP: "bg-purple-500/20 text-purple-300 border-purple-500/40",
  Employee: "bg-orange-500/20 text-orange-300 border-orange-500/40",
  Sponsor: "bg-yellow-500/20 text-yellow-300 border-yellow-500/40",
  General: "bg-slate-600/30 text-slate-300 border-slate-500/40",
};
const EMPTY = { first_name: "", last_name: "", email: "", phone: "", guest_type: "General", notes: "" };
const field = "bg-slate-900 border-slate-700 text-slate-100";

export function GuestFormModal({ open, onClose, guest, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF(guest ? { ...EMPTY, ...guest, guest_type: GUEST_TYPES.includes(guest.guest_type) ? guest.guest_type : "General" } : EMPTY); } }, [open, guest]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { first_name: f.first_name, last_name: f.last_name, email: f.email, phone: f.phone, guest_type: f.guest_type, notes: f.notes };
    try {
      if (guest) await api.put(`/guests/${guest.id}`, body);
      else await api.post("/guests", body);
      toast.success(guest ? "Guest updated" : "Guest added");
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#131D33] border-slate-700 text-slate-100 max-w-lg" data-testid="guest-form-modal">
        <DialogHeader><DialogTitle className="font-display text-2xl">{guest ? "Edit Guest" : "Add Guest"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>First name *</Label><Input required value={f.first_name} onChange={set("first_name")} className={field} data-testid="guest-first-name-input" /></div>
            <div><Label>Last name *</Label><Input required value={f.last_name} onChange={set("last_name")} className={field} data-testid="guest-last-name-input" /></div>
            <div><Label>Email</Label><Input type="email" value={f.email} onChange={set("email")} className={field} data-testid="guest-email-input" /></div>
            <div><Label>Phone</Label><Input value={f.phone} onChange={set("phone")} className={field} data-testid="guest-phone-input" /></div>
          </div>
          <div>
            <Label>Ticket Type</Label>
            <select value={f.guest_type} onChange={set("guest_type")} className={`w-full rounded-md border px-3 py-2 text-sm ${field}`} data-testid="guest-type-select">
              {GUEST_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div><Label>Notes</Label><Textarea value={f.notes} onChange={set("notes")} className={field} rows={2} data-testid="guest-notes-input" /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="bg-slate-900 border-slate-700" data-testid="guest-form-cancel">Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="guest-form-submit">{busy ? "Saving…" : "Save Guest"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
